import React, { useMemo } from 'react'
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Box } from '@mui/material'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import { TimeRange } from 'models'
import EmptyScreen from 'packages/ui/EmptyScreen'
import ChartSkeleton from 'components/global/CUK/Skeletons/ChartSkeleton'

import useGraph from './use-graph'
import useUtils from '../use-utils'
import TooltipGraph from '../components/TooltipGraph'
import Legend from '../components/Legend'
import { Stack } from '@mui/system'

interface NetworkActiveUsersProps {
  modalTimeRange?: TimeRange
  hasLegend: boolean
}

const GraphActiveUsers: React.FC<NetworkActiveUsersProps> = ({ modalTimeRange, hasLegend = false }) => {
  const { t } = useTranslation()
  const { loading, graph } = useGraph({ modalTimeRange: modalTimeRange as TimeRange })
  const { customTickFormatter } = useUtils()

  const chartData = useMemo(() => {
    return graph.map(({ date, networks, is_estimated, average_per_device_type }, index) => {
      const baseData = {
        timestamp: moment(date).unix() * 1000,
        is_estimated,
        average_per_device_type
      }

      const values = {
        private: networks.filter(({name}) => name === 'private')
          .reduce((sum, network) => sum + network.active_users, 0),
        guest: networks.filter(({name}) => name === 'guest')
          .reduce((sum, network) => sum + network.active_users, 0),
        other: networks.filter(({name}) => name === 'other')
          .reduce((sum, network) => sum + network.active_users, 0),
      }

      const lastPointWasActual = index > 0 && !graph[index - 1].is_estimated
      const showOnEstimatedLine = is_estimated || lastPointWasActual

      const dataKeys = {}
      Object.entries(values).forEach(([key, value]) => {
        dataKeys[key] = !is_estimated ? value : null
        dataKeys[`${key}_estimated`] = showOnEstimatedLine ? value : null
      })

      return {
        ...baseData,
        ...dataKeys
      }
    })
  }, [graph])
  
  const hasData = useMemo(() => {
    if (!chartData.length) return false
    
    return chartData.some(entry => 
      (entry.private > 0) || 
      (entry.guest > 0) || 
      (entry.other > 0) ||
      (entry.private_estimated > 0) || 
      (entry.guest_estimated > 0) || 
      (entry.other_estimated > 0)
    )
  }, [chartData])

  const legendItems = useMemo(() => {
    return [
      { label: t('stats.legends.types.guest'), color: '#EC7A74' },
      { label: t('stats.legends.types.private'), color: '#4F8AA4' },
      { label: t('stats.legends.types.other'), color: '#A5D9CB' }
    ]
  }, [])

  if (loading) {
    return <ChartSkeleton />
  }

  if (!hasData) {
    return (
      <Box sx={{ 
        width: '100%',
        height: '100%',
        border: '1px dashed #D7DAEA',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px'
      }}>
        <EmptyScreen 
          icon="square-poll-vertical" 
          variant="compact"
          iconSize="32px" 
          fontSize="14px"
          spacing={1.5}
        >
          {t('stats.traffic.graphs.no_data')}
        </EmptyScreen>
      </Box>
    )
  }

  const tooltipPosition = modalTimeRange ? { y: -120 } : { y: -140 }

  return (
    <Stack gap={1} height={'100%'}>
      <ResponsiveContainer>
        <LineChart
          width={500}
          height={300}
          data={chartData}
          margin={{
            top: 60,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <XAxis
            dataKey="timestamp"
            tickFormatter={customTickFormatter}
            style={{
              fontSize: '0.75rem',
              fontWeight: 400,
            }}
          />
          <YAxis
            style={{
              fontSize: '0.75rem',
              fontWeight: 400,
            }}
          />
          <Tooltip
            cursor={false}
            wrapperStyle={{ outline: 'none', zIndex: 9999 }}
            content={
              <TooltipGraph
                dataType={'type'}
                isTraffic={true}
              />
            }
            position={tooltipPosition}
          />
          <Line
            dataKey="other"
            connectNulls={false}
            fill="#A5D9CB"
            stroke="#A5D9CB"
            strokeWidth={2}
            dot={false}
          />
          <Line
            dataKey="private"
            connectNulls={false}
            fill="#4F8AA4"
            stroke="#4F8AA4"
            strokeWidth={2}
            dot={false}
          />
          <Line
            dataKey="guest"
            connectNulls={false}
            fill="#EC7A74"
            stroke="#EC7A74"
            strokeWidth={2}
            dot={false}
          />
          <Line
            dataKey="other_estimated"
            connectNulls={false}
            fill="#A5D9CB"
            stroke="#A5D9CB"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
          />
          <Line
            dataKey="private_estimated"
            connectNulls={false}
            fill="#4F8AA4"
            stroke="#4F8AA4"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
          />
          <Line
            dataKey="guest_estimated"
            connectNulls={false}
            fill="#EC7A74"
            stroke="#EC7A74"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
      {
        hasLegend && <Legend items={legendItems} />
      }
    </Stack>
  )
}

export default GraphActiveUsers